import { useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { apiRequest } from "../../../../../shared/api/client";
import type { RequestDetailData, RequestOfferEvaluation } from "../../../domain/request-detail-types";
import type { SpecialistDecisionInput } from "../../../application/commands/submit-specialist-decision";
import { createSubmitSpecialistDecision } from "../../../application/commands/submit-specialist-decision";
import { procurementCommandGateway } from "../../../infrastructure/api/procurement-command-gateway";
import type { RequestDetailScope } from "./useRequestDetailScope";
import { requestDecisionPresentation } from "./request-decision-presentation";

type Options = {scope: RequestDetailScope; detail: RequestDetailData | null; evaluation: RequestOfferEvaluation | null; setDetail: Dispatch<SetStateAction<RequestDetailData | null>>; setEvaluation: Dispatch<SetStateAction<RequestOfferEvaluation | null>>; onRequestRoutedForApproval?: () => void};
type Draft = {scope: RequestDetailScope; open: boolean; step: 1 | 2; offerId: string; rationale: string; error: string; evaluationId: string | null; version: number | null};
const empty = (scope: RequestDetailScope): Draft => ({scope, open: false, step: 1, offerId: "", rationale: "", error: "", evaluationId: null, version: null});
type Kind = "evaluation" | "decision" | "route";
export function useRequestDecisionWorkspace({scope, detail, evaluation, setDetail, setEvaluation, onRequestRoutedForApproval}: Options) {
  const [stored, setStored] = useState(() => empty(scope));
  const draft = stored.scope === scope ? stored : empty(scope);
  const revision = useRef(0), busy = useRef(new Set<RequestDetailScope>());
  const version = revision.current;
  const latest = useRef({detail, evaluation, onRequestRoutedForApproval});
  latest.current = {detail, evaluation, onRequestRoutedForApproval};
  const [activity, setActivity] = useState<{scope: RequestDetailScope; kind: Kind | null}>({scope, kind: null});
  const [evaluationFailure, setEvaluationFailure] = useState<{scope: RequestDetailScope; message: string} | null>(null);
  const current = () => scope.isCurrent() && latest.current.detail?.id === scope.requestNo;
  const isCurrentDraft = () => current() && version === revision.current;
  const patch = (update: (current: Draft) => Draft, changesDraft = true) => {
    if (!scope.isCurrent()) return;
    if (changesDraft) revision.current++;
    setStored(value => update(value.scope === scope ? value : empty(scope)));
  };
  const setDecisionOpen: Dispatch<SetStateAction<boolean>> = value => patch(current => ({...current, open: typeof value === "function" ? value(current.open) : value}));
  const setDecisionStep: Dispatch<SetStateAction<1 | 2>> = value => patch(current => ({...current, step: typeof value === "function" ? value(current.step) : value}));
  const setDecisionOfferId: Dispatch<SetStateAction<string>> = value => patch(current => ({...current, offerId: typeof value === "function" ? value(current.offerId) : value}));
  const setDecisionRationale: Dispatch<SetStateAction<string>> = value => patch(current => ({...current, rationale: typeof value === "function" ? value(current.rationale) : value}));
  const setError = (error: string) => {if (isCurrentDraft()) patch(current => ({...current, error}), false);};
  const openDecision = () => {
    if (!current() || !evaluation) return;
    const firstEligible = evaluation.offers.find(offer => !offer.exclusionReasons.length);
    patch(current => ({...current, open: true, step: 1, offerId: evaluation.topOfferId ?? firstEligible?.id ?? "", rationale: "", error: "", evaluationId: evaluation.id, version: evaluation.version}));
  };
  const begin = (kind: Kind) => {
    if (!current() || busy.current.has(scope)) return false;
    busy.current.add(scope); setActivity({scope, kind}); return true;
  };
  const finish = () => {busy.current.delete(scope); if (scope.isCurrent()) setActivity({scope, kind: null});};
  const refreshEvaluation = async () => {
    if (!begin("evaluation")) return;
    setEvaluationFailure(null);
    try {
      const result = await apiRequest<RequestOfferEvaluation>(`/api/requests/${encodeURIComponent(scope.requestNo)}/evaluations`, {method: "POST", body: JSON.stringify({})});
      if (!scope.isCurrent()) return;
      setEvaluation(result);
      scope.notify(["COMPLETED", "SINGLE_OFFER"].includes(result.status) && result.offers.some(offer => !offer.exclusionReasons.length) ? "Teklif değerlendirmesi güncellendi. Karar verebilirsiniz." : "Değerlendirme güncellendi; karar vermeyi engelleyen koşullar sürüyor.");
    } catch (error) {
      if (!scope.isCurrent()) return;
      const message = error instanceof Error ? error.message : "Teklif değerlendirmesi yenilenemedi.";
      setEvaluationFailure({scope, message});
      scope.notify(message);
    }
    finally {finish();}
  };
  const routeForApproval = async () => {
    if (!begin("route")) return;
    let routed = false;
    try {
      await apiRequest(`/api/requests/${encodeURIComponent(scope.requestNo)}/route-approval`, {method: "POST"}); routed = true;
      if (!scope.isCurrent()) return;
      scope.notify("Talep dinamik onay akışına gönderildi.");
      latest.current.onRequestRoutedForApproval?.();
      if (!scope.isCurrent()) return;
      setDetail(await apiRequest<RequestDetailData>(`/api/requests/${encodeURIComponent(scope.requestNo)}`));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Talep onaya gönderilemedi.";
      scope.notify(routed ? `Talep onaya gönderildi, ancak ekran yenilenemedi: ${message}` : message);
    } finally {finish();}
  };
  const matchesEvaluation = () => latest.current.evaluation?.id === draft.evaluationId && latest.current.evaluation?.version === draft.version;
  const submitDecision = async () => {
    if (!isCurrentDraft() || busy.current.has(scope) || !evaluation) return;
    setError("");
    if (!matchesEvaluation()) {setError("Değerlendirme yenilendi; karar ekranını kapatıp yeniden açın."); return;}
    if (!draft.offerId) {setError("Onaya göndermek için bir teklif seçin."); return;}
    const selected = evaluation.offers.find(offer => offer.id === draft.offerId);
    if (!selected || selected.exclusionReasons.length || !["COMPLETED", "SINGLE_OFFER"].includes(evaluation.status)) {setError("Seçilen teklif güncel değerlendirmede karara uygun değil."); return;}
    const rationale = draft.rationale.trim();
    if (rationale.length < 10) {setError("Karar gerekçesi en az 10 karakter olmalıdır."); return;}
    if (!begin("decision")) return;
    let routed = false, recommendationSaved = false;
    const submitSpecialistDecision = createSubmitSpecialistDecision({
      async saveRecommendation<T>(input: SpecialistDecisionInput) {const value = await procurementCommandGateway.saveRecommendation<T>(input); recommendationSaved = true; return value;},
      routeToManager: requestNo => procurementCommandGateway.routeToManager(requestNo),
    });
    try {
      const decisionType = evaluation.status === "SINGLE_OFFER" ? "SINGLE_SOURCE" : draft.offerId === evaluation.topOfferId ? "ACCEPT_SYSTEM" : "SELECT_ALTERNATIVE";
      await submitSpecialistDecision({requestNo: scope.requestNo, evaluationId: evaluation.id, decisionType, offerId: decisionType === "ACCEPT_SYSTEM" ? undefined : draft.offerId, rationale}); routed = true;
      if (!scope.isCurrent()) return;
      if (isCurrentDraft()) setDecisionOpen(false);
      scope.notify("Uzman kararı kaydedildi ve Satın Alma Müdürüne iletildi.");
      latest.current.onRequestRoutedForApproval?.();
      if (!scope.isCurrent()) return;
      setDetail(await apiRequest<RequestDetailData>(`/api/requests/${encodeURIComponent(scope.requestNo)}`));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Karar gönderilemedi.";
      if (routed) scope.notify(`Uzman kararı iletildi, ancak ekran yenilenemedi: ${message}`);
      else {
        const description = recommendationSaved ? `Uzman önerisi kaydedildi, ancak onay akışı tamamlanamadı. Kaydı yenileyip kontrol edin: ${message}` : message;
        setError(description); scope.notify(`Hata: ${description}`);
      }
    } finally {finish();}
  };
  const kind = activity.scope === scope ? activity.kind : null;
  return {decisionOpen: draft.open, decisionStep: draft.step, decisionOfferId: draft.offerId, decisionRationale: draft.rationale,
    decisionError: draft.open && !matchesEvaluation() ? "Değerlendirme yenilendi; karar ekranını kapatıp yeniden açın." : draft.error,
    setDecisionOpen, setDecisionStep, setDecisionOfferId, setDecisionRationale, openDecision, submitDecision, routeForApproval, refreshEvaluation,
    evaluationError: evaluationFailure?.scope === scope ? evaluationFailure.message : "",
    refreshingEvaluation: kind === "evaluation", submittingDecision: kind === "decision", requestDecisionBusy: kind !== null,
    ...requestDecisionPresentation(detail, evaluation, draft.offerId),
  };
}
